import styled from '@emotion/styled'
import { Fragment, FunctionComponent } from 'react'
import { Color, Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { WithClassName } from '~/types/utils'
import { Icon, IconName } from './Icon'
import { Legend } from './Legend'

type ChartTooltipProps = WithClassName & {
  date: string
  metrics: ChartTooltipMetricProps[][]
}

export type ChartTooltipMetricProps = WithClassName & {
  iconName?: IconName
  iconColor?: Color
  label: string
  value: string
  secondary?: boolean
}

const ChartTooltipWrapper = styled.div`
  display: inline-block;
  width: 300px;
  padding: 12px 0;
`

const ChartTooltipMetric = styled.div<{ secondary?: boolean }>`
  & ${Icon} {
    margin-right: 6px;
  }

  color: ${({ secondary }) => (secondary ? Colors.SlateGrey : Colors.Jet)};

  ${Fonts.P2};
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-style: ${({ secondary }) => (secondary ? 'italic' : 'normal')};

  &:not(:last-of-type) {
    margin-bottom: 6px;
  }
`

const ChartToolTipMetricValue = styled.div`
  font-weight: bold;
`

const ChartToolTipMetricSeparator = styled.hr`
  border: 0;
  border-top: 1px solid ${Colors.Platinum};
`

const ChartTooltipTitle = styled.div`
  padding: 0 12px;
  margin-bottom: 12px;
`

const ChartTooltipMetricGroup = styled.div`
  padding: 0 12px;
`

const BaseChartTooltip: FunctionComponent<ChartTooltipProps> = ({ date, className, metrics }) => {
  return (
    <ChartTooltipWrapper className={className}>
      <ChartTooltipTitle>{date}</ChartTooltipTitle>
      {metrics.map((metricGroup, index) => {
        return (
          <Fragment key={`group-${index}`}>
            <ChartTooltipMetricGroup>
              {metricGroup.map((metric, i) => {
                return (
                  <ChartTooltipMetric key={`metric-${i}`} secondary={metric.secondary}>
                    <Legend label={metric.label} iconName={metric.iconName} iconColor={metric.iconColor ?? Colors.Jet} fontColor={metric.secondary ? Colors.SlateGrey : Colors.Jet} />

                    <ChartToolTipMetricValue>{metric.value}</ChartToolTipMetricValue>
                  </ChartTooltipMetric>
                )
              })}
            </ChartTooltipMetricGroup>
            {index !== metrics.length - 1 && <ChartToolTipMetricSeparator />}
          </Fragment>
        )
      })}
    </ChartTooltipWrapper>
  )
}

export const ChartTooltip = styled(BaseChartTooltip)``

export const generateChartTooltip = ({ date, metrics }: Omit<ChartTooltipProps, 'className'>): string => {
  const iconSize = 16
  const sigmaSvg = (color: string) => `
    <svg viewBox='0 0 14 14' width='${iconSize}' height='${iconSize}' style='display:inline-block;vertical-align:middle;margin-right:6px;' xmlns='http://www.w3.org/2000/svg'>
      <path fill='none' stroke='${color}' d='M12 0.5H2L8 6.5L2 12.5H12' />
    </svg>
  `
  const dataLineSvg = (color: string) => `
    <span style=" margin-right: 6px;flex-shrink: 0;">
      <svg viewBox="0 0 14 14"  width='${iconSize}' height='${iconSize}' xmlns="http://www.w3.org/2000/svg">
          <path fill="${color}" fill-rule="evenodd" clip-rule="evenodd" d="M1.45788 13.9255C3.58373 13.6955 5.03771 12.9402 6.03902 11.7774C7.16398 10.471 7.58854 8.77065 7.94528 7.34193L7.94528 7.34193L7.97014 7.24241C8.35797 5.69111 8.69022 4.44052 9.47652 3.5274C10.1986 2.68883 11.4413 1.99987 14 1.99987C14 1.08319 13.3833 0.310544 12.5421 0.0742188C10.4163 0.304231 8.96229 1.05954 7.96098 2.22235C6.83602 3.52875 6.41146 5.2291 6.05472 6.65782L6.05472 6.65782L6.02986 6.75734C5.64203 8.30864 5.30978 9.55922 4.52348 10.4724C3.80138 11.3109 2.55871 11.9999 0 11.9999C0 12.9166 0.616721 13.6892 1.45788 13.9255Z"/>
      </svg>
    </span>
  `

  const squareSvg = (color: string, size: number) =>
    `<span style=" margin-right: 6px;flex-shrink: 0;">
        <svg viewBox="0 0 14 14" width="${size}" height="${size}" style="display:inline-block;vertical-align:middle;margin-right:6px;flex-shrink:0;" xmlns="http://www.w3.org/2000/svg"><rect width="14" height="14" rx="2" fill="${color}"/></svg>
  </span>`

  const emptySvg = () => `<span><svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor"></svg></span>`

  const metricGroups = metrics
    .map((metricGroup, groupIdx) => {
      const rows = metricGroup
        .map((metric) => {
          let iconHtml = ''
          if (!metric.iconName) metric.iconName = 'square'
          if (metric.iconName === 'sigma') {
            iconHtml = sigmaSvg(metric.iconColor ?? Colors.Jet)
          } else if (metric.iconName === 'data_line') {
            iconHtml = dataLineSvg(metric.iconColor ?? Colors.Jet)
          } else if (metric.iconName === 'square') {
            iconHtml = squareSvg(metric.iconColor ?? Colors.Jet, iconSize)
          } else if (metric.iconName === 'empty') {
            iconHtml = emptySvg()
          }
          return `
        <div style="margin-bottom: 6px; color: rgb(54, 54, 54);font-size: 0.875rem;line-height: 22px;display: flex;-webkit-box-pack: justify;justify-content: space-between;-webkit-box-align: center;align-items: center;font-style: ${metric.secondary ? 'italic' : 'normal'};">
          <span style="display: flex; align-items: center; color: ${metric.secondary ? Colors.SlateGrey : Colors.Jet};">
            ${iconHtml}
            ${metric.label}
          </span>
          <span style="font-weight: bold;">${metric.value}</span>
        </div>
      `
        })
        .join('')
      return `
      <div style="padding: 0 12px;">
        ${rows}
      </div>
      ${groupIdx !== metrics.length - 1 ? `<hr style=\"border: 0; border-top: 1px solid ${Colors.Platinum}; margin: 8px 0;\" />` : ''}
    `
    })
    .join('')

  return `
    <div style="display: inline-block;width: 300px;padding: 12px 0;  font-family: 'Roboto', sans-serif;">
      <div style="padding: 0 12px;margin-bottom: 12px;">${date}</div>
      ${metricGroups}
    </div>
  `
}
